<script setup>
import { computed, ref, watch } from 'vue'
import { useELDStore } from '@/stores/ELDs'
import { useUiStore } from '@/stores/UiStore'
import { useToastStore } from '@/stores/Toast'
import { SEND_ELD } from '@/constants/dispatch'

/**
 * Pinia Store instances.
 */
const uiStore = useUiStore()
const eldStore = useELDStore()
const toastStore = useToastStore()

/**
 * Local refs.
 */
const elds = ref([])
const isLoading = ref(false)
const selectedElds = ref([])

/**
 * Load data.
 *
 * @return {Object}
 */
const data = computed(() => {
    return uiStore.actionLoads(SEND_ELD)
})

/**
 * Determine if the dialog should be shown.
 *
 * @return {Boolean}
 */
const show = computed(() => {
    return uiStore.isActionActive(SEND_ELD)
})

/**
 * Send the load to the selected ELD.
 *
 * @return {Promise}
 */
const onAccept = async () => {
    try {
        isLoading.value = true

        const promises = Promise.all(
            selectedElds.value.map((eld) => {
                return eldStore.sendToELD(data.value.id, eld)
            })
        )

        await promises

        toastStore.setNotification({
            message: 'Load sent to ELD',
            type: 'success',
        })
    } catch (error) {
        toastStore.setNotification({
            message: error.message(),
            type: 'error',
        })
    } finally {
        isLoading.value = false

        onCancel()
    }
}

/**
 * Close the dialog.
 *
 * @return {void}
 */
const onCancel = () => {
    uiStore.closeAction(SEND_ELD)
}

/**
 * Watch for changes to the selected load.
 */
watch(
    () => data.value,
    async (load) => {
        try {
            if (!load?.id) {
                return
            }

            const response = await eldStore.get(load.id)

            elds.value = response.get('data', [])

            if (elds.value.length > 0) {
                selectedElds.value.push(elds.value[0].id)
            }
        } catch {
            toastStore.setNotification({
                message: 'Failed to load ELDs',
                type: 'error',
            })
        }
    },
    { immediate: true }
)
</script>

<template>
    <v-dialog
        data-test="confirmation-dialog"
        v-model="show"
        persistent
        width="480"
    >
        <v-card class="!rounded-lg">
            <v-card-item
                title="Send Load to Eld"
                density="compact"
                class="!py-2 !px-4 bg-gray-100 border-b"
            >
                <template v-slot:append>
                    <v-btn
                        size="x-small"
                        variant="text"
                        icon="fal fa-close"
                        color="primary"
                        class="rounded-md"
                        @click="onCancel"
                    >
                    </v-btn>
                </template>
            </v-card-item>
            <v-card-text class="!px-4" data-test="confirmation-dialog-body">
                <v-form>
                    <div class="text-base my-4">
                        Select the electronic logging devices to send the load
                        with PRO # {{ data?.id }}
                        to:
                    </div>
                    <v-checkbox
                        v-for="eld in elds"
                        :key="eld.id"
                        v-model="selectedElds"
                        :label="eld.name"
                        :value="eld.id"
                        variant="outlined"
                        density="compact"
                        hide-details
                    ></v-checkbox>
                </v-form>
            </v-card-text>
            <div class="bg-gray-100 border-t py-2 px-4">
                <span class="text-xs">
                    The load will be sent to the selected electronic logging
                    device.
                </span>
            </div>
            <v-spacer class="border-b"></v-spacer>
            <v-card-actions class="px-4 justify-end">
                <v-btn
                    data-test="confirmation-dialog-btn-close"
                    variant="outlined"
                    @click="onCancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    :loading="isLoading"
                    :disabled="!selectedElds.length"
                    color="primary"
                    variant="flat"
                    data-test="confirmation-dialog-btn-accept"
                    @click="onAccept"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
